import type { MarkerDef } from './types'

/**
 * Marcadores laboratoriais da metodologia Mãe Nutrida para CRIANÇAS.
 * Fonte: planilha `Exames Laboratoriais - Crianças.xlsx`.
 *
 * A planilha original possui uma única coluna "Valor Ideal" que vale para
 * todas as faixas etárias da metodologia (0-6m / 6m-2a / 2-10a / 10-18a).
 * Por isso o range é registrado em `default` — o seletor de faixa etária
 * no formulário continua existindo (será usado pela Nina como contexto na
 * análise), mas não altera a faixa verde de cada marcador.
 */
export const CRIANCA_MARKERS: MarkerDef[] = [
  {
    id: 'hemoglobina',
    name: 'Hemoglobina',
    group: 'Eritrograma e Leucograma',
    obs: '> 12 até o VR do laboratório',
    ranges: {
      default: { min: 12, display: '>12g/dL', unit: 'g/dL' },
    },
  },
  {
    id: 'hematocrito',
    name: 'Hematócrito',
    group: 'Eritrograma e Leucograma',
    ranges: {
      default: { display: '3 x Hemoglobina' },
    },
  },
  {
    id: 'eritrocitos',
    name: 'Eritrócitos',
    group: 'Eritrograma e Leucograma',
    ranges: {
      default: { min: 4, max: 5.5, display: '4 - 5,5 milhões/mm³', unit: 'milhões/mm³' },
    },
  },
  {
    id: 'vcm',
    name: 'VCM',
    group: 'Eritrograma e Leucograma',
    ranges: {
      default: { min: 80, max: 85, display: '80 - 85 fL', unit: 'fL' },
    },
  },
  {
    id: 'hcm',
    name: 'HCM',
    group: 'Eritrograma e Leucograma',
    ranges: {
      default: { min: 27, max: 30, display: '27 - 30 pg', unit: 'pg' },
    },
  },
  {
    id: 'chcm',
    name: 'CHCM',
    group: 'Eritrograma e Leucograma',
    ranges: {
      default: { min: 31, max: 34, display: '31 - 34 f/dL' },
    },
  },
  {
    id: 'rdw',
    name: 'RDW',
    group: 'Eritrograma e Leucograma',
    ranges: {
      default: { min: 12, max: 13, display: '12 - 13%', unit: '%' },
    },
  },
  {
    id: 'leucocitos',
    name: 'Leucócitos',
    group: 'Eritrograma e Leucograma',
    ranges: {
      default: { min: 5000, max: 7000, display: '5000 - 7000mm³', unit: 'mm³' },
    },
  },
  {
    id: 'neutrofilos',
    name: 'Neutrófilos',
    group: 'Eritrograma e Leucograma',
    ranges: {
      default: { max: 45, display: 'até 45%', unit: '%' },
    },
  },
  {
    id: 'monocitos',
    name: 'Monócitos',
    group: 'Eritrograma e Leucograma',
    ranges: {
      default: { min: 6, max: 7, display: '6 a 7%', unit: '%' },
    },
  },
  {
    id: 'eosinofilos',
    name: 'Eosinófilos',
    group: 'Eritrograma e Leucograma',
    ranges: {
      default: { max: 2, display: '< 2%', unit: '%' },
    },
  },
  {
    id: 'basofilos',
    name: 'Basófilos',
    group: 'Eritrograma e Leucograma',
    ranges: {
      default: { max: 0.5, display: '< 0,5%', unit: '%' },
    },
  },
  {
    id: 'plaquetas',
    name: 'Plaquetas',
    group: 'Eritrograma e Leucograma',
    ranges: {
      default: { min: 140000, max: 500000, display: '140.000 - 500.000mm³', unit: 'mm³' },
    },
  },
  {
    id: 'ferro-serico',
    name: 'Ferro sérico',
    group: 'Avaliação do ferro',
    ranges: {
      default: { min: 80, max: 110, display: '80-110mg/dl', unit: 'mg/dl' },
    },
  },
  {
    id: 'ferritina',
    name: 'Ferritina',
    group: 'Avaliação do ferro',
    obs: 'Valores considerados com PCR dentro do ideal',
    ranges: {
      default: { min: 50, max: 70, display: '50- 70ng/ml', unit: 'ng/ml' },
    },
  },
  {
    id: 'pcr-us',
    name: 'PCR us',
    group: 'Avaliação do ferro',
    ranges: {
      default: { max: 0.25, display: '<0,25mg/dl', unit: 'mg/dl' },
    },
  },
  {
    id: 'saturacao-de-transferrina',
    name: 'Saturação de transferrina',
    group: 'Avaliação do ferro',
    ranges: {
      default: { min: 22, max: 35, display: '22-35%', unit: '%' },
    },
  },
  {
    id: 'glicemia-jejum',
    name: 'Glicemia jejum',
    group: 'Glicêmico',
    ranges: {
      default: { min: 80, max: 85, display: '80 - 85mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'insulina-jejum',
    name: 'Insulina jejum',
    group: 'Glicêmico',
    ranges: {
      default: { max: 10, display: '<10µU/mL', unit: 'µU/mL' },
    },
  },
  {
    id: 'colesterol-total',
    name: 'Colesterol total',
    group: 'Lipídico',
    ranges: {
      default: { display: '170 mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'ldl',
    name: 'LDL',
    group: 'Lipídico',
    ranges: {
      default: { max: 110, display: '< 110 mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'hdl',
    name: 'HDL',
    group: 'Lipídico',
    obs: 'ideal ligeiramente acima do VR',
    ranges: {
      default: { min: 45, display: '> 45 mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'triglicerideos',
    name: 'Triglicerídeos (0 - 9 anos)',
    group: 'Lipídico',
    ranges: {
      default: { max: 75, display: '<75 mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'triglicerideos-2',
    name: 'Triglicerídeos (10 - 19 anos)',
    group: 'Lipídico',
    ranges: {
      default: { max: 90, display: '<90 mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'nao-hdl-c',
    name: 'Não HDL – C',
    group: 'Lipídico',
    ranges: {
      default: { max: 120, display: '<120 mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'apolipoproteina-b',
    name: 'Apolipoproteína B',
    group: 'Lipídico',
    ranges: {
      default: { max: 90, display: '<90 mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'homocisteina',
    name: 'Homocisteína',
    group: 'Nutricional (homocisteína, B12, folato)',
    ranges: {
      default: { min: 5, max: 8, display: '5 – 8 mcmol/L', unit: 'mcmol/L' },
    },
  },
  {
    id: 'vitamina-b12',
    name: 'Vitamina B12',
    group: 'Nutricional (homocisteína, B12, folato)',
    ranges: {
      default: { min: 500, display: '>500pg/mL', unit: 'pg/mL' },
    },
  },
  {
    id: 'acido-metilmalonico',
    name: 'Ácido metilmalônico',
    group: 'Nutricional (homocisteína, B12, folato)',
    ranges: {
      default: { max: 0.5, display: '<0,5mmol/L', unit: 'mmol/L' },
    },
  },
  {
    id: 'folato-serico',
    name: 'Folato sérico',
    group: 'Nutricional (homocisteína, B12, folato)',
    ranges: {
      default: { min: 9, max: 15, display: '9 a 15ng/mL', unit: 'ng/mL' },
    },
  },
  {
    id: 'folato-eritrocitario',
    name: 'Folato eritrocitário',
    group: 'Nutricional (homocisteína, B12, folato)',
    ranges: {
      default: { min: 151, display: '>151 ng/ml', unit: 'ng/ml' },
    },
  },
  {
    id: 'ureia',
    name: 'Ureia',
    group: 'Função renal',
    ranges: {
      default: { min: 11, max: 38, display: '11-38 mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'creatinina',
    name: 'creatinina',
    group: 'Função renal',
    ranges: {
      default: { min: 0.4, max: 0.9, display: '0,4 a 0,9 mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'tsh',
    name: 'TSH',
    group: 'Tireoide',
    ranges: {
      default: { min: 1, max: 2.5, display: '1 – 2,5 UI/mL', unit: 'UI/mL' },
    },
  },
  {
    id: 't4-livre',
    name: 'T4 livre',
    group: 'Tireoide',
    ranges: {
      default: { min: 0.9, max: 1.3, display: '0,9 a 1,3 ng/mL', unit: 'ng/mL' },
    },
  },
  {
    id: 'anti-tpo',
    name: 'Anti TPO',
    group: 'Tireoide',
    ranges: {
      default: { display: '< VR' },
    },
  },
  {
    id: 'anti-tg',
    name: 'Anti TG',
    group: 'Tireoide',
    ranges: {
      default: { display: '< VR' },
    },
  },
  {
    id: 'tgo',
    name: 'TGO',
    group: 'Função hepática',
    ranges: {
      default: { max: 25, display: 'até 25 U/L', unit: 'U/L' },
    },
  },
  {
    id: 'tgp',
    name: 'TGP',
    group: 'Função hepática',
    ranges: {
      default: { max: 20, display: 'até 20 U/L', unit: 'U/L' },
    },
  },
  {
    id: 'ige-total',
    name: 'IgE total',
    group: 'Imunidade',
    ranges: {
      default: { display: '< VR do laboratório' },
    },
  },
  {
    id: 'iga',
    name: 'IgA',
    group: 'Imunidade',
    ranges: {
      default: { display: 'VR do laboratório' },
    },
  },
  {
    id: 'selenio-serico',
    name: 'Selênio sérico',
    group: 'Nutricional',
    ranges: {
      default: { display: 'Terço superior' },
    },
  },
  {
    id: 'selenio-eritrocitario',
    name: 'Selênio eritrocitário',
    group: 'Nutricional',
    ranges: {
      default: { display: '2° tercil' },
    },
  },
  {
    id: 'cobre-serico',
    name: 'Cobre sérico',
    group: 'Nutricional',
    ranges: {
      default: { min: 100, max: 120, display: '100 – 120mcg/dL', unit: 'mcg/dL' },
    },
  },
  {
    id: 'ceruloplasmina',
    name: 'Ceruloplasmina',
    group: 'Nutricional',
    ranges: {
      default: { display: 'seguir VR do lab.' },
    },
  },
  {
    id: 'somatomedina-c',
    name: 'Somatomedina C',
    group: 'Nutricional',
    obs: 'varia muito com faixa etária',
    ranges: {
      default: { display: 'Terceiro e Quarto quintil' },
    },
  },
  {
    id: 'magnesio',
    name: 'Magnésio',
    group: 'Nutricional',
    ranges: {
      default: { min: 2, display: '>2,0mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'vitamina-a',
    name: 'Vitamina A',
    group: 'Nutricional',
    obs: 'Faixa varia por idade — mantida literal por bucket',
    ranges: {
      '0-6m': { min: 0.47, max: 0.65, display: '0,47 a 0,65 mg/L (0-6 anos)', unit: 'mg/L' },
      '6m-2a': { min: 0.47, max: 0.65, display: '0,47 a 0,65 mg/L (0-6 anos)', unit: 'mg/L' },
      '2-10a': { min: 0.47, max: 0.81, display: '0,47-0,81 mg/L (0-12 anos)', unit: 'mg/L' },
      '10-18a': { min: 0.7, max: 0.98, display: '0,7 a 0,98 mg/L (13-17 anos)', unit: 'mg/L' },
      default: { min: 0.47, max: 0.98, display: '0-6 anos: 0,47-0,65 mg/L · 7-12 anos: 0,57-0,81 mg/L · 13-17 anos: 0,7-0,98 mg/L', unit: 'mg/L' },
    },
  },
  {
    id: 'vitamina-c',
    name: 'Vitamina C',
    group: 'Nutricional',
    obs: 'Terço superir',
    ranges: {
      default: { min: 1.46, display: '>1,46mg/dl', unit: 'mg/dl' },
    },
  },
  {
    id: '25ohvit-d',
    name: '25(OH)vit D',
    group: 'Nutricional',
    ranges: {
      default: { min: 40, display: '>40ng/mL', unit: 'ng/mL' },
    },
  },
  {
    id: 'zinco-serico',
    name: 'Zinco sérico',
    group: 'Nutricional',
    ranges: {
      default: { min: 90, display: '>90mcg/dL', unit: 'mcg/dL' },
    },
  },
  {
    id: 'zinco-eritrocitario',
    name: 'Zinco eritrocitário',
    group: 'Nutricional',
    ranges: {
      default: { min: 660, display: '>660mcg/dL', unit: 'mcg/dL' },
    },
  },
  {
    id: 'pth',
    name: 'PTH',
    group: 'Nutricional',
    ranges: {
      default: { min: 25, max: 45, display: '25 a 45pg/mL', unit: 'pg/mL' },
    },
  },
  {
    id: 'iodo-urinario-de-24h',
    name: 'iodo urinário de 24h:',
    group: 'Nutricional',
    ranges: {
      default: { min: 100, max: 199, display: '100 – 199mcg/L', unit: 'mcg/L' },
    },
  },
  {
    id: 'tireoglobulina',
    name: 'Tireoglobulina',
    group: 'Nutricional',
    ranges: {
      default: { max: 13, display: '<13mcg/L', unit: 'mcg/L' },
    },
  },
  {
    id: 'calcio-ionico',
    name: 'Cálcio iônico',
    group: 'Nutricional',
    ranges: {
      default: { min: 4.5, max: 5.2, display: '4,5-5,2 mg/dl', unit: 'mg/dl' },
    },
  },
  {
    id: 'calcio-serico',
    name: 'Cálcio sérico',
    group: 'Nutricional',
    ranges: {
      default: { min: 8.5, max: 10.5, display: '8,5 a 10,5 mg/dL', unit: 'mg/dL' },
    },
  },
  {
    id: 'aluminio',
    name: 'Alumínio',
    group: 'Metais pesados',
    ranges: {
      default: { max: 2, display: 'até 2,0' },
    },
  },
  {
    id: 'chumbo',
    name: 'Chumbo',
    group: 'Metais pesados',
    ranges: {
      default: { display: 'seguir VR do lab.' },
    },
  },
  {
    id: 'cadmio',
    name: 'Cádmio',
    group: 'Metais pesados',
    ranges: {
      default: { display: 'seguir VR do lab.' },
    },
  },
  {
    id: 'mercurio',
    name: 'Mercúrio',
    group: 'Metais pesados',
    ranges: {
      default: { display: 'seguir VR do lab.' },
    },
  },
  {
    id: 'arsenico',
    name: 'Arsênico',
    group: 'Metais pesados',
    ranges: {
      default: { display: 'seguir VR do lab.' },
    },
  },
  {
    id: 'melatonina-salivar-diurno',
    name: 'Melatonina Salivar (Diurno)',
    group: 'Hormonal',
    ranges: {
      default: { max: 5, display: '< 5 pg/ml', unit: 'pg/ml' },
    },
  },
  {
    id: 'melatonina-salivar-noturno',
    name: 'Melatonina Salivar (Noturno)',
    group: 'Hormonal',
    ranges: {
      default: { min: 10, display: '> 10 pg/ml', unit: 'pg/ml' },
    },
  },
]
